import { useCallback, useEffect, useState } from "react";
import { ApiError, resumeChat, sendChat } from "./api";
import { ChatMessage } from "../components/MessageBubble";
import { deriveTitle, getConversation, saveConversation, StoredConversation } from "./conversations";
import { clearThreadId, getThreadId, setThreadId } from "./storage";

let seq = 0;
function nextId(): string {
  seq += 1;
  return `m${seq}-${Date.now()}`;
}

/**
 * Owns one conversation's message list, active environment, and the
 * interrupt/resume flow. status=interrupted from the backend means the
 * agent asked a clarifying question (send_question_to_human); the next user
 * message is routed to /v1/chat/resume instead of /v1/chat until answered.
 *
 * Conversations are persisted to localStorage (per user) after every turn so
 * the sidebar history and env selection survive a page reload — this never
 * touches the server; the backend remains the source of truth only for the
 * checkpointed graph state behind a given thread_id.
 */
export function useChat(token: string, userId: string, defaultEnv: string, onAuthError: () => void) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [threadId, setThreadIdState] = useState<string | undefined>(
    () => getThreadId() ?? undefined,
  );
  const [env, setEnv] = useState<string>(defaultEnv);
  const [awaitingClarification, setAwaitingClarification] = useState(false);
  const [busy, setBusy] = useState(false);

  // 首次挂载：若本地存有上次活跃的会话，恢复其消息与环境（纯本地，不请求服务端）
  useEffect(() => {
    const activeThreadId = getThreadId();
    if (!activeThreadId) return;
    const stored = getConversation(userId, activeThreadId);
    if (!stored) return;
    setMessages(stored.messages);
    setEnv(stored.env);
    setAwaitingClarification(stored.awaitingClarification);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const persist = useCallback(
    (tid: string, msgs: ChatMessage[], envValue: string, awaiting: boolean) => {
      const conv: StoredConversation = {
        threadId: tid,
        title: deriveTitle(msgs),
        env: envValue,
        awaitingClarification: awaiting,
        updatedAt: Date.now(),
        messages: msgs,
      };
      saveConversation(userId, conv);
    },
    [userId],
  );

  const appendSystem = useCallback((content: string) => {
    setMessages((prev) => [...prev, { id: nextId(), role: "system", content }]);
  }, []);

  const send = useCallback(
    async (text: string) => {
      const userMsg: ChatMessage = { id: nextId(), role: "user", content: text };
      const pendingId = nextId();
      const optimistic = [...messages, userMsg, { id: pendingId, role: "assistant" as const, content: "", pending: true }];
      setMessages(optimistic);
      setBusy(true);

      try {
        const result = awaitingClarification
          ? await resumeChat(token, threadId!, text)
          : await sendChat(token, text, threadId, env);

        setThreadIdState(result.thread_id);
        setThreadId(result.thread_id);

        const nowInterrupted = result.status === "interrupted";
        const finalMessages = optimistic.map((m) =>
          m.id === pendingId
            ? {
                ...m,
                content: nowInterrupted
                  ? result.question ?? "需要您补充信息："
                  : result.reply ?? "（无回复内容）",
                pending: false,
              }
            : m,
        );
        setAwaitingClarification(nowInterrupted);
        setMessages(finalMessages);
        persist(result.thread_id, finalMessages, env, nowInterrupted);
      } catch (err) {
        setMessages((prev) => prev.filter((m) => m.id !== pendingId));
        if (err instanceof ApiError && err.status === 401) {
          onAuthError();
          return;
        }
        appendSystem(
          `⚠️ ${err instanceof Error ? err.message : "请求失败，请重试"}`,
        );
      } finally {
        setBusy(false);
      }
    },
    [awaitingClarification, threadId, token, env, messages, onAuthError, appendSystem, persist],
  );

  const newConversation = useCallback(() => {
    setMessages([]);
    setThreadIdState(undefined);
    setAwaitingClarification(false);
    setEnv(defaultEnv);
    clearThreadId();
  }, [defaultEnv]);

  const loadConversation = useCallback(
    (tid: string) => {
      const stored = getConversation(userId, tid);
      if (!stored) return;
      setMessages(stored.messages);
      setThreadIdState(stored.threadId);
      setEnv(stored.env);
      setAwaitingClarification(stored.awaitingClarification);
      setThreadId(stored.threadId);
    },
    [userId],
  );

  return {
    messages,
    send,
    busy,
    awaitingClarification,
    newConversation,
    loadConversation,
    threadId,
    env,
    setEnv,
  };
}
